import type { ReactNode } from 'react';
import { useRef, useCallback } from 'react';
import {
  preventOverflow,
  offset,
  popperOffsets,
  popperGenerator,
  applyStyles,
  eventListeners,
  computeStyles,
  arrow,
} from '@popperjs/core';
import type { Instance } from '@popperjs/core';

import { cn } from '@simplywallst/ui-core/next';
import { useIsMounted } from '@simplywallst/ui-core';

type Props = {
  content?: ReactNode;
  children: ReactNode;
  show: boolean;
};

const createPopper = popperGenerator({
  defaultOptions: {
    placement: 'top-end',
    strategy: 'fixed',
  },
  defaultModifiers: [
    eventListeners,
    computeStyles,
    applyStyles,
    popperOffsets,
    preventOverflow,
    arrow,
  ],
});

export const Tooltip = ({ children, content, show }: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const boundaryRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<Instance | null>(null);
  const isMounted = useIsMounted();

  const cleanUpPopper = () => {
    const popper = popperRef.current;
    if (popper !== null) {
      popper.destroy();
      popperRef.current = null;
    }
  };

  const handlePopperRef = useCallback(
    (node: HTMLDivElement) => {
      if (!node || !anchorRef.current) {
        cleanUpPopper();
        return;
      }
      const currentPopper = popperRef.current;
      if (currentPopper) {
        currentPopper.destroy();
        popperRef.current = null;
      }

      const popper = createPopper(anchorRef.current, node, {
        modifiers: [
          {
            ...offset,
            options: {
              offset: [0, 12],
            },
          },
          {
            ...preventOverflow,
            options: {
              boundary: boundaryRef,
            },
          },
        ],
      });
      popperRef.current = popper;
    },
    [anchorRef, boundaryRef, isMounted]
  );

  return (
    <div ref={boundaryRef} className="relative z-50">
      <div
        role="tooltip"
        ref={handlePopperRef}
        className={cn(
          show ? 'pointer-events-auto visible' : 'pointer-events-none invisible'
        )}
      >
        <div
          ref={tooltipRef}
          className={cn('relative w-max rounded-lg bg-p-blue-100')}
        >
          {content}
          <div
            data-popper-arrow
            className="absolute top-full size-0 border-x-8 border-t-8 border-solid border-x-transparent border-b-transparent border-t-p-blue-100"
          />
        </div>
      </div>
      <div ref={anchorRef}>{children}</div>
    </div>
  );
};
